import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { exportIds } from '../../config/config';

const PAINT_DELAY_MS = 500;

function isDesktopSafari(): boolean {
  const ua = navigator.userAgent;
  return (
    /safari/i.test(ua) &&
    !/chrome|chromium|crios|android/i.test(ua) &&
    !/iphone|ipad|ipod/i.test(ua)
  );
}

function openPdfInNewTab(pdf: jsPDF, filename: string): void {
  const blob = pdf.output('blob');
  const blobUrl = URL.createObjectURL(blob);

  const newTab = window.open(blobUrl, '_blank');
  if (newTab) {
    newTab.document.title = filename;
  } else {
    const anchorElement = document.createElement('a');
    anchorElement.href = blobUrl;
    anchorElement.download = filename;
    anchorElement.style.display = 'none';
    document.body.appendChild(anchorElement);
    anchorElement.click();
    anchorElement.remove();
  }

  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, 10_000);
}

async function savePdf(pdf: jsPDF, filename: string): Promise<void> {
  if (!isDesktopSafari()) {
    pdf.save(filename);
    return;
  }

  let anchorElement: HTMLAnchorElement | null = null;
  let blobUrl: string | null = null;
  let pdfBlob: Blob | null = null;
  let attemptedAnchorDownload = false;

  try {
    pdfBlob = pdf.output('blob');
    blobUrl = URL.createObjectURL(pdfBlob);

    anchorElement = document.createElement('a');
    anchorElement.href = blobUrl;
    anchorElement.download = filename;
    anchorElement.style.display = 'none';
    document.body.appendChild(anchorElement);
    anchorElement.click();
    attemptedAnchorDownload = true;
  } catch {

  } finally {
    setTimeout(() => {
      anchorElement?.remove();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    }, 10_000);
  }

  if (!attemptedAnchorDownload) {
    openPdfInNewTab(pdf, filename);
  }
}

async function svgToPngDataUrl(svgDataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || 24;
      const h = img.naturalHeight || 24;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('No canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = svgDataUrl;
  });
}

async function prepareImagesForCapture(element: HTMLElement): Promise<void> {
  const frontendOrigin = window.location.origin;
  const images = element.querySelectorAll<HTMLImageElement>('img[src]');

  const promises = Array.from(images).map(async (img) => {
    const src = img.getAttribute('src');
    if (!src || /^(data:|blob:)/i.test(src)) return;

    const absoluteSrc = src.startsWith('/') ? frontendOrigin + src : src;
    const isSameOrigin = absoluteSrc.startsWith(frontendOrigin);
    const isSvg = /\.svg$/i.test(src);

    if (isSvg) {
      try {
        const response = await fetch(absoluteSrc, { mode: 'cors' });
        if (!response.ok) return;
        const blob = await response.blob();
        let dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        if (blob.type === 'image/svg+xml') {
          dataUrl = await svgToPngDataUrl(dataUrl);
        }
        img.src = dataUrl;
        await new Promise<void>((resolve) => {
          if (img.complete) resolve();
          else img.onload = () => resolve();
        });
      } catch {
      }
      return;
    }

    if (isSameOrigin) {
      return;
    }

    img.crossOrigin = 'anonymous';
    const originalSrc = img.src;
    img.src = '';
    img.src = originalSrc;
    await new Promise<void>((resolve) => {
      if (img.complete) resolve();
      else {
        img.onload = () => resolve();
        img.onerror = () => resolve();
      }
    });
  });

  await Promise.all(promises);
}

export async function captureElementToPdf(
  element: HTMLElement,
  filename: string
): Promise<void> {
  await new Promise((r) => setTimeout(r, PAINT_DELAY_MS));

  await prepareImagesForCapture(element);

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    scrollY: 0,
    scrollX: 0,
    imageTimeout: 15000,
    onclone: (clonedDoc, clonedElement) => {
      clonedElement.style.visibility = 'visible';
      clonedElement.style.opacity = '1';
      const root = clonedDoc.getElementById(exportIds.pdfExportRoot);
      if (root) {
        root.style.visibility = 'visible';
        root.style.opacity = '1';
      }
    },
  });

  const imgData = canvas.toDataURL('image/png');
  const imgWidth = 210;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [imgWidth, imgHeight],
  });
  pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

  await savePdf(pdf, filename);
}